import * as React from 'react';

import moment from 'moment';
import {RRule} from 'rrule';
import {capitalize} from 'underscore.string';

import type {Assignable} from 'nutshell-core/types';

import {
    RecurrenceMode,
    type GetDefaultTask_taskDefaults as TaskDefaults,
} from 'nutshell-graphql-types';

import {colors} from '../../colors/colors';
import {HourglassRegularIcon} from '../../icon/svg/hourglass-regular-icon';
import {InfoIcon} from '../../icon/svg/info-icon';
import {PipelineRegularIcon} from '../../icon/svg/pipeline-regular-icon';
import {SkipArrowIcon} from '../../icon/svg/skip-arrow-icon';
import {TaskRegularAiIcon} from '../../icon/svg/task-regular-ai-icon';
import {TaskTickCompleteIcon} from '../../icon/svg/task-tick-complete';
import {UnskipArrowIcon} from '../../icon/svg/unskip-arrow-icon';
import type {ToolbarOption} from '../../toolbar/toolbar';

import {RRULE_WEEKDAYS} from './task-custom-recurrence-popover/consts';
import type {CycleUnit} from './task-custom-recurrence-popover/task-custom-recurrence-popover';
import type {RecurrenceRule} from './task-recurrence-rule';
import type {TaskRowItem} from './types';

export const getAdditionalInfo = (
    isCompleted: boolean,
    canBeCompleted: boolean | null | undefined,
    requirementText: string | null | undefined,
    isAutomated: boolean,
    isSkipped?: boolean | null,
    isAiGenerated?: boolean,
    creatorEntityName?: string | null
): React.ReactElement | null => {
    if (isAiGenerated) {
        const tooltip = creatorEntityName
            ? `Generated from<br/>${creatorEntityName}<br/>as a follow-up`
            : 'AI-generated follow-up task';

        return (
            <div data-tip={tooltip} data-place='bottom' data-multiline={true}>
                {/* @ts-expect-error */}
                <TaskRegularAiIcon size={20} color={colors.aiPurplePrimary} />
            </div>
        );
    } else if (!canBeCompleted && requirementText && isAutomated && !isCompleted) {
        return (
            <div data-tip={requirementText} data-place='bottom'>
                <InfoIcon
                    size={15}
                    // @ts-expect-error
                    color={colors.blue}
                    useCurrentColor={true}
                    wrapWithDiv={false}
                />
            </div>
        );
    } else if (isAutomated || isSkipped) {
        // @ts-expect-error
        return <PipelineRegularIcon size={14} color={colors.orange} />;
    } else {
        return null;
    }
};

export const getAutomatedOptions = (
    isSkippable: boolean | null | undefined,
    isSkipped: boolean | null | undefined,
    isDelayable: boolean | null | undefined,
    isCompleteable: boolean | null | undefined,
    isCompleted: boolean | null | undefined,
    onSkipStep: () => void,
    onDelayStep: () => void,
    onCompleteStep: () => void,
    onUnskipStep: () => void,
    skipButtonRef: {
        current: HTMLElement | null | undefined;
    },
    unskipButtonRef: {
        current: HTMLElement | null | undefined;
    },
    delayButtonRef: {
        current: HTMLElement | null | undefined;
    }
): ToolbarOption[] => {
    const options = [];
    if (isSkippable && !isSkipped && !isCompleted) {
        options.push({
            id: 'skip',
            tooltipText: 'Skip task',
            icon: SkipArrowIcon,
            onClick: () => {
                onSkipStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                skipButtonRef.current = ref;
            },
        });
    }
    if (isDelayable && !isCompleted && !isSkipped) {
        options.push({
            id: 'delay',
            tooltipText: 'Delay task',
            icon: HourglassRegularIcon,
            onClick: () => {
                onDelayStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                delayButtonRef.current = ref;
            },
        });
    }
    if (isCompleteable && isSkipped && !isCompleted) {
        options.push({
            id: 'unskip',
            tooltipText: 'Unskip task',
            icon: UnskipArrowIcon,
            onClick: () => {
                onUnskipStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                unskipButtonRef.current = ref;
            },
        });
        options.push({
            id: 'complete',
            tooltipText: 'Complete task',
            icon: TaskTickCompleteIcon,
            onClick: () => {
                onCompleteStep();
            },
        });
    }

    return options;
};

// Get the assignee name given an assignee id
export const getAssigneeName = (assignables: Assignable[], assigneeId: string) => {
    const foundAssignee = assignables.find((a) => a.id === assigneeId);

    return foundAssignee ? foundAssignee.name : assigneeId;
};

export const mapTaskToDefaultTask = (task: TaskRowItem): TaskDefaults => {
    return {
        assignee: task.assignee,
        dueTime: task.dueTime,
        description: task.description,
        title: task.title,
        completedByUser: task.completedByUser,
        completedTime: task.completedTime,
        id: task.id,
        isOverdue: task.isOverdue,
        recurrenceRule: task.__typename === 'Task' ? task.recurrenceRule : null,
        __typename: 'Task', // Will only ever be a Task
    };
};

export function getDueDateOccurrence(
    referenceDatetime: number | null | undefined,
    dueDateCycleValue: number,
    dueDateCycleUnit: CycleUnit,
    dueDateWeekday: number,
    dueDateOccurrence: number
): string {
    const weekday = RRULE_WEEKDAYS[dueDateWeekday] ?? null;
    const baseUnix = referenceDatetime ?? moment().unix();
    const baseMoment = moment.unix(baseUnix);
    const utcMoment = baseMoment.utc();
    const dtstart = new Date(
        Date.UTC(
            utcMoment.year(),
            utcMoment.month(),
            utcMoment.date(),
            utcMoment.hour(),
            utcMoment.minute(),
            utcMoment.second()
        )
    );

    let freq = RRule.MONTHLY;
    switch (dueDateCycleUnit) {
        case 'day':
            freq = RRule.DAILY;
            break;
        case 'week':
            freq = RRule.WEEKLY;
            break;
    }

    // Daily recurrence doesn't use a weekday; weekly pins the weekday and
    // monthly pins the nth occurrence of that weekday.
    let byweekday;
    if (weekday && dueDateCycleUnit !== 'day') {
        if (dueDateCycleUnit === 'week') {
            byweekday = [weekday];
        } else {
            byweekday = [weekday.nth(dueDateOccurrence)];
        }
    }

    const rule = new RRule({
        freq,
        interval: dueDateCycleValue,
        ...(byweekday ? {byweekday} : {}),
        dtstart: dtstart,
    });
    return rule.toString();
}

function getCompletionModeRule(
    completionCycleValue: number,
    completionCycleUnit: CycleUnit
): string {
    const rule = new RRule({
        freq: completionCycleUnit === 'week' ? RRule.WEEKLY : RRule.MONTHLY,
        interval: completionCycleValue,
    });
    return rule.toString();
}

export const getRecurrenceRule = (
    mode: RecurrenceMode,
    referenceDatetime: number | null | undefined,
    completionCycleValue: number,
    completionCycleUnit: CycleUnit,
    dueDateCycleValue: number,
    dueDateCycleUnit: CycleUnit,
    dueDateWeekday: number,
    dueDateOccurrence: number
) => {
    if (mode === RecurrenceMode.COMPLETION) {
        return getCompletionModeRule(completionCycleValue, completionCycleUnit);
    } else {
        return getDueDateOccurrence(
            referenceDatetime,
            dueDateCycleValue,
            dueDateCycleUnit,
            dueDateWeekday,
            dueDateOccurrence
        );
    }
};

// The rrule library's `.toText()` doesn't know about our COMPLETION mode, so we
// build the display string ourselves for tasks that recur relative to when the
// prior task is completed (e.g. "Weekly after the prior task").
function getCompletionModeText(ruleString: string): string {
    const rule = RRule.fromString(ruleString);
    const interval = rule.options.interval;

    if (rule.options.freq === RRule.WEEKLY) {
        return interval === 1
            ? 'Weekly after the prior task'
            : `Every ${interval} weeks after the prior task`;
    }

    if (rule.options.freq === RRule.MONTHLY) {
        return interval === 1
            ? 'Monthly after the prior task'
            : `Every ${interval} months after the prior task`;
    }

    return `${capitalize(rule.toText())} after the prior task`;
}

export function getHumanReadableRecurrenceRule(recurrenceRule: RecurrenceRule | null): string {
    if (!recurrenceRule) {
        return 'Does not repeat';
    }

    if (recurrenceRule.mode === RecurrenceMode.COMPLETION) {
        return getCompletionModeText(recurrenceRule.rule);
    }

    return capitalize(RRule.fromString(recurrenceRule.rule).toText());
}
